// page.service.js
import config from 'app/config';
import { saveItem, deleteItemByKey } from './storage.service'; // Assuming storage.service is correctly implemented

class PageService {

  // Ensure all dependencies are correctly injected for minification
  static $inject = ["$http", "$location", '$state', '$q', '$rootScope'];

  /** @ngInject */
  constructor($http, $location, $state, $q, $rootScope) {

    Object.assign(this, {
      $http, $location, $state, $q, $rootScope
    });

    this.config = config;
    // Correct: Initialize _globalPageCache and _globalPageDeferred as INSTANCE PROPERTIES
    this._globalPageCache = new Map();
    this._globalPageDeferred = this.$q.defer(); // Initialize a new deferred on service creation
  }

  // setup default headers to be used with UC Checkout API.
  headers() {
    return {
      headers: {
        'content-type': 'application/json',
        'Accept': "application/json",
        'Authorization': 'Basic b3BtcnM6QmZMek9yWGJzMzNDdE5i'
      }
    };
  }

  // Query Params : read all the query param and return as object
  getQueryParamObject() {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());
    return params;
  }

  // --- API Call Methods (Converted to async/await) ---

  async getPage(page) {
    try {
      const response = await this.$http.get(this.config.api + page);
      return response.data;
    } catch (error) {
      console.error(`PageService: Error fetching page from ${page}:`, error);
      throw error;
    }
  }

  async getRow(endpoint) {
    try {
      const response = await this.$http.get(this.config.api + endpoint);
      return response.data;
    } catch (error) {
      console.error(`PageService: Error fetching row from ${endpoint}:`, error);
      throw error;
    }
  }

  async getRowV3(endpoint) {
    try {
      const response = await this.$http.get(this.config.apiv3 + endpoint);
      return response.data;
    } catch (error) {
      console.error(`PageService: Error fetching rowV3 from ${endpoint}:`, error);
      throw error;
    }
  }

  async post(endpoint, data) {
    try {
      const response = await this.$http.post(this.config.api + endpoint, data);
      return response.data;
    } catch (error) {
      console.error(`PageService: Error posting to ${endpoint}:`, error);
      throw error;
    }
  }

  async postV3(endpoint, data) {
    try {
      const response = await this.$http.post(this.config.apiv3 + endpoint, data, this.headers());
      return response.data;
    } catch (error) {
      console.error(`PageService: Error posting V3 to ${endpoint}:`, error);
      throw error;
    }
  }

  async getTC(id) {
    try {
      const response = await this.$http.get(this.config.api + 'checkout/tc/' + id);
      return response.data;
    } catch (error) {
      console.error(`PageService: Error fetching TC for ${id}:`, error);
      throw error;
    }
  }

  async logTC(data) {
    try {
      const result = await this.$http.post(this.config.api + 'checkout/tc/log', data);
      if (result.data?.status === 'ok') {
        saveItem(`${config.appName}_atcid`, result.data);
      }
      return result.data;
    } catch (error) {
      console.error("PageService: Error logging TC:", error);
      throw error;
    }
  }

  async traffic(launch) {
    try {
      const qParams = this.getQueryParamObject();
      const affId = typeof qParams.AFFID !== "undefined" && qParams.AFFID != true ? qParams.AFFID : 0;
      const data = {
        affiliate_id: affId,
        page: this.$state.$current.data?.pageTitle,
        page_url: this.$location.absUrl(),
        launch: launch
      };
      await this.$http.post(this.config.api + 'partners/log/traffic', data);
    } catch (error) {
      console.error("PageService: Error logging traffic:", error);
    }
  }

  async optin(launch, name, email) {
    try {
      const qParams = this.getQueryParamObject();
      const affId = typeof qParams.AFFID !== "undefined" && qParams.AFFID != true ? qParams.AFFID : 0;
      const data = {
        affiliate_id: affId,
        page: this.$state.$current.data?.pageTitle,
        page_url: this.$location.absUrl(),
        launch: launch,
        name: name,
        email: email
      };
      await this.$http.post(this.config.api + 'partners/log/optins', data);
    } catch (error) {
      console.error("PageService: Error logging optin:", error);
    }
  }

  // RECOMMENDATION: Move this to a separate `utils.service.js` or `base64.util.js`
  base64_encode(e) {
    var r, t, c, a, h, n, o, A, i = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/=", d = 0, l = 0, s = "", u = [];
    if (!e)
      return e;
    do
      r = e.charCodeAt(d++), t = e.charCodeAt(d++), c = e.charCodeAt(d++), A = r << 16 | t << 8 | c, a = A >> 18 & 63, h = A >> 12 & 63, n = A >> 6 & 63, o = 63 & A, u[l++] = i.charAt(a) + i.charAt(h) + i.charAt(n) + i.charAt(o);
    while (d < e.length);
    s = u.join("");
    var C = e.length % 3;
    var decoded = (C ? s.slice(0, C - 3) : s) + "===".slice(C || 3);
    decoded = decoded.replace("+", "-");
    decoded = decoded.replace("/", "_");
    return decoded;
  }

  async updateLogTC(atcid, orderid) {
    try {
      const data = { id: atcid, orderid: orderid };
      const result = await this.$http.put(this.config.api + 'checkout/tc/log', data);
      if (result.data?.status === 'ok') {
        console.log('TC Order ID Updated');
        deleteItemByKey(`${config.appName}_atcid`);
      }
      return result.data;
    } catch (error) {
      console.error("PageService: Error updating TC log:", error);
      throw error;
    }
  }

  /**
   * Sets the global page object. This should be called by your dynamic route loader
   * after successfully fetching the page data.
   * It resolves any pending promises from getGlobalPage().
   * @param {object} pageData The dynamic page object fetched from the API.
   */
  setGlobalPage(pageData) {
    if (!pageData) {
      console.warn("PageService: Attempted to set null or undefined page data.");
      return;
    }
    this._globalPageCache.set('current', pageData); // Use 'this.'
    this.$rootScope.page = pageData;
    this._globalPageDeferred.resolve(pageData); // Use 'this.'
  }

  /**
   * Gets the global page object.
   * If the page data is already available, it returns the object directly.
   * If the page data is still loading, it returns a promise that resolves
   * when the page data becomes available.
   * @returns {object|Promise<object>} The page object or a promise that resolves with it.
   */
  getGlobalPage() {
    if (this._globalPageCache.has('current')) { // Use 'this.'
      return this._globalPageCache.get('current'); // Use 'this.'
    } else {
      return this._globalPageDeferred.promise; // Use 'this.'
    }
  }

  /**
   * Resets the global page state. This should be called when navigating to a new
   * dynamic route, to ensure that components waiting for the page data will
   * wait for the *new* page data to be set.
   */
  resetGlobalPage() {
    this._globalPageCache.delete('current'); // Use 'this.'
    this.$rootScope.page = null;
    this._globalPageDeferred = this.$q.defer(); // Use 'this.' - create a brand new deferred
  }
}

export { PageService }; // Export the class, the module then registers it by string name